import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Target, Globe, Banknote, ArrowRight, Sparkles, Brain, Zap, CheckCircle2, Star, ChevronRight, Clock, TrendingUp, Gift, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const fr = language === 'fr';

  const goStart = () => navigate(user ? '/quick-quiz' : '/register');

  return (
    <div className="min-h-screen bg-[#FAFAF9] overflow-hidden">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-lg font-semibold text-stone-800">Ikigai</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setLanguage(fr ? 'en' : 'fr')}
              className="flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 transition-colors px-2 py-1.5 bg-stone-100 rounded-full"
              data-testid="language-toggle"
            >
              <span>{fr ? '🇬🇧' : '🇫🇷'}</span>
              <span>{fr ? 'EN' : 'FR'}</span>
            </button>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-[#FF6B6B] hover:bg-[#EE5253] text-white text-xs px-3 py-1.5 h-auto rounded-full" data-testid="dashboard-btn">Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-stone-600 hover:text-stone-900 text-xs px-2 py-1.5 h-auto" data-testid="login-btn">{t('login')}</Button>
                <Button onClick={() => navigate('/register')} className="bg-[#FF6B6B] hover:bg-[#EE5253] text-white text-xs px-3 py-1.5 h-auto rounded-full" data-testid="register-btn">{t('register')}</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO — Emotional Impact */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4">
        {/* Subtle animated bg circles */}
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#FF6B6B]/5 blur-3xl animate-pulse-slow pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#4ECDC4]/5 blur-3xl animate-pulse-slow pointer-events-none" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Urgency badge */}
          <div className="inline-flex items-center gap-2 bg-[#1A535C] text-white px-5 py-2.5 rounded-full text-sm font-semibold mb-8 animate-fade-in">
            <Zap className="w-4 h-4" />
            {fr ? "L'IA qui révèle ta vocation" : "AI that reveals your calling"}
          </div>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight mb-6 animate-slide-up" data-testid="hero-title">
            {t('heroTitle')}
          </h1>
          
          <p className="text-base md:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto mb-8" style={{animationDelay: '0.1s'}}>
            {t('heroSubtitle')}
          </p>

          {/* Price anchor */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <Button onClick={goStart} className="btn-primary flex items-center gap-3 text-lg px-10 py-5 shadow-xl hover:shadow-2xl" data-testid="hero-cta-btn">
              {t('startJourney')}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 text-sm">
              <Gift className="w-4 h-4 text-[#4ECDC4]" />
              <span className="text-stone-500">{fr ? "Parcours gratuit" : "Free journey"}</span>
              <span className="text-stone-300">|</span>
              <span className="font-semibold text-[#FF6B6B]">{fr ? "Diagnostic IA : 1,99\u00A0\u20AC" : "AI Diagnostic: \u20AC1.99"}</span>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-stone-500">
            <div className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#4ECDC4]" />{fr ? "Paiement sécurisé" : "Secure payment"}</div>
            <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#FFE66D]" />{fr ? "Résultat en 5 min" : "Result in 5 min"}</div>
            <div className="flex items-center gap-1.5"><Brain className="w-4 h-4 text-[#FF6B6B]" />{fr ? "IA Claude (Anthropic)" : "Claude AI (Anthropic)"}</div>
          </div>
        </div>
      </section>

      {/* SECTION: Le problème — Agitation */}
      <section className="py-16 md:py-20 px-4 bg-[#1A535C]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
            {fr ? "Tu te lèves chaque matin avec cette question..." : "You wake up every morning with this question..."}
          </h2>
          <p className="text-xl md:text-2xl text-[#4ECDC4] font-serif italic mb-10">
            {fr ? "\"Est-ce vraiment ça, ma vie ?\"" : "\"Is this really it? Is this my life?\""}
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
            {(fr ? [
              "Tu ne sais pas quel métier est fait pour toi",
              "Tu as l'impression de gaspiller ton potentiel",
              "Tu hésites entre plusieurs chemins sans oser choisir",
              "Tu envies ceux qui ont trouvé leur passion",
              "Tu as peur de regretter dans 10 ans",
              "Tu sens que tu mérites mieux que ça"
            ] : [
              "You don't know which career is right for you",
              "You feel like you're wasting your potential",
              "You hesitate between paths without daring to choose",
              "You envy those who found their passion",
              "You're afraid of regret in 10 years",
              "You feel you deserve better than this"
            ]).map((pain, i) => (
              <div key={`k-${i}`} className="flex items-center gap-3 text-left bg-white/10 rounded-xl px-4 py-3">
                <ChevronRight className="w-4 h-4 text-[#FF6B6B] flex-shrink-0" />
                <span className="text-white/90 text-sm">{pain}</span>
              </div>
            ))}
          </div>
          
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {fr 
              ? "Ces doutes, cette frustration... C'est normal. Mais rester dans le flou, c'est laisser les autres décider de ton avenir."
              : "These doubts, this frustration... It's normal. But staying in the dark means letting others decide your future."}
          </p>
        </div>
      </section>

      {/* SECTION: La solution — Ce que tu obtiens */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#FF6B6B]/10 text-[#FF6B6B] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              {fr ? "Ce que tu obtiens" : "What you get"}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 mb-4">
              {fr ? "Ton avenir, révélé par l'intelligence artificielle" : "Your future, revealed by artificial intelligence"}
            </h2>
            <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
              {fr 
                ? "En 40 questions profondes, l'IA analyse qui tu es vraiment et te montre le chemin."
                : "In 40 deep questions, AI analyzes who you really are and shows you the path."}
            </p>
          </div>

          {/* What the AI reveals */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-14">
            {[
              { icon: Heart, color: '#FF6B6B', title: fr ? "Tes passions profondes" : "Your deep passions", desc: fr ? "Ce qui te fait vibrer, même quand personne ne regarde. L'IA identifie ce qui te rend vivant." : "What makes you vibrate, even when no one's watching. AI identifies what makes you alive." },
              { icon: Target, color: '#FFE66D', title: fr ? "Tes talents cachés" : "Your hidden talents", desc: fr ? "Ces capacités que tu sous-estimes. L'IA révèle tes forces que même toi tu ne voyais pas." : "Those abilities you underestimate. AI reveals strengths you didn't even see." },
              { icon: Globe, color: '#4ECDC4', title: fr ? "Ta mission de vie" : "Your life mission", desc: fr ? "Le problème du monde que TU es fait pour résoudre. L'IA connecte tes valeurs à ton impact." : "The world problem YOU are made to solve. AI connects your values to your impact." },
              { icon: Banknote, color: '#1A535C', title: fr ? "5 pistes de carrière" : "5 career paths", desc: fr ? "Des métiers concrets, alignés avec qui tu es. Plus de doutes. Juste de la clarté." : "Concrete careers aligned with who you are. No more doubts. Just clarity." },
            ].map((item, i) => (
              <div key={`k-${i}`} className="flex gap-5 p-6 bg-stone-50 rounded-2xl border border-stone-100 hover:border-stone-200 hover:shadow-lg transition-all duration-300 group">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{backgroundColor: `${item.color}15`}}>
                  <item.icon className="w-7 h-7" style={{color: item.color}} />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-stone-900 mb-1">{item.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Additional deliverables */}
          <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="font-serif text-xl font-bold text-stone-900 text-center mb-6">
              {fr ? "Ton rapport Ikigai complet inclut aussi :" : "Your full Ikigai report also includes:"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {(fr ? [
                "Profil de personnalité détaillé",
                "3 projets concrets à tester dès demain",
                "Environnements de travail idéaux",
                "Diagramme Ikigai interactif",
                "Export PDF de tes résultats",
                "Coaching IA quotidien personnalisé"
              ] : [
                "Detailed personality profile",
                "3 concrete projects to try tomorrow",
                "Ideal work environments",
                "Interactive Ikigai diagram",
                "PDF export of your results",
                "Daily personalized AI coaching"
              ]).map((item, i) => (
                <div key={`k-${i}`} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-[#4ECDC4] flex-shrink-0" />
                  <span className="text-stone-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Comment ça marche — 3 étapes */}
      <section className="py-16 md:py-20 px-4 bg-[#FAFAF9]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 mb-4">
              {fr ? "3 étapes pour changer ton avenir" : "3 steps to change your future"}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: fr ? "Réponds" : "Answer", desc: fr ? "40 questions profondes, à ton rythme. Pas de bonne ou mauvaise réponse. Juste toi, en toute authenticité." : "40 deep questions, at your pace. No right or wrong answers. Just you, authentically.", color: '#FF6B6B', tag: fr ? 'Gratuit' : 'Free' },
              { step: "02", title: fr ? "Découvre" : "Discover", desc: fr ? "L'IA analyse tes réponses et génère ton profil Ikigai unique : passions, talents, carrières, projets concrets." : "AI analyzes your answers and generates your unique Ikigai profile: passions, talents, careers, concrete projects.", color: '#4ECDC4', tag: '1,99 €' },
              { step: "03", title: fr ? "Agis" : "Act", desc: fr ? "Reçois ton Plan d'Action 90 Jours personnalisé. 12 semaines de tâches concrètes pour transformer ta vie." : "Get your personalized 90-Day Action Plan. 12 weeks of concrete tasks to transform your life.", color: '#1A535C', tag: '4,99 €' },
            ].map((item, i) => (
              <div key={`k-${i}`} className="relative text-center group">
                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-white font-serif text-2xl font-bold transition-transform group-hover:scale-110" style={{backgroundColor: item.color}}>
                  {item.step}
                </div>
                <div className="inline-block bg-white px-3 py-1 rounded-full text-xs font-semibold mb-3 border" style={{color: item.color, borderColor: `${item.color}40`}}>
                  {item.tag}
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION: Plan 90 jours — Upsell */}
      <section className="py-16 md:py-20 px-4 bg-[#1A535C]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <TrendingUp className="w-4 h-4" />
                {fr ? "Plan d'Action 90 Jours" : "90-Day Action Plan"}
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
                {fr ? "Ne reste pas bloqué après le diagnostic" : "Don't stay stuck after the diagnostic"}
              </h2>
              <p className="text-white/70 mb-6 leading-relaxed">
                {fr 
                  ? "Savoir qui tu es, c'est bien. Mais sans plan d'action, rien ne change. L'IA te crée un programme personnalisé de 12 semaines pour passer du rêve à la réalité."
                  : "Knowing who you are is great. But without an action plan, nothing changes. AI creates a personalized 12-week program to go from dream to reality."}
              </p>
              <div className="space-y-3 mb-8">
                {(fr ? [
                  "Semaines 1-4 : Exploration — Valider tes pistes",
                  "Semaines 5-8 : Construction — Développer tes compétences",
                  "Semaines 9-12 : Lancement — Passer à l'action"
                ] : [
                  "Weeks 1-4: Exploration — Validate your paths",
                  "Weeks 5-8: Building — Develop your skills",
                  "Weeks 9-12: Launch — Take action"
                ]).map((phase, i) => (
                  <div key={`k-${i}`} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#4ECDC4] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white/90 text-sm">{phase}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 text-center border border-white/20">
              <p className="text-[#4ECDC4] text-sm font-semibold mb-2">{fr ? "ACHAT ADDITIONNEL" : "ADD-ON PURCHASE"}</p>
              <div className="font-serif text-5xl font-bold text-white mb-2">4,99 <span className="text-2xl">€</span></div>
              <p className="text-white/50 text-sm mb-6">{fr ? "Paiement unique, pas d'abonnement" : "One-time payment, no subscription"}</p>
              <div className="space-y-2 text-left mb-6">
                {(fr ? [
                  "12 semaines structurées par l'IA",
                  "3 tâches concrètes par semaine",
                  "Jalons de progression",
                  "Export PDF du plan complet"
                ] : [
                  "12 weeks structured by AI",
                  "3 concrete tasks per week",
                  "Progress milestones",
                  "Full plan PDF export"
                ]).map((f, i) => (
                  <div key={`k-${i}`} className="flex items-center gap-2 text-white/80 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#4ECDC4] flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
              <Button onClick={goStart} className="w-full bg-[#4ECDC4] hover:bg-[#45b8b0] text-[#1A535C] font-bold py-4 rounded-xl text-base" data-testid="plan90-cta-btn">
                {fr ? "Commencer maintenant" : "Start now"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Pricing summary */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 mb-3">
              {fr ? "Combien vaut la clarté sur ton avenir ?" : "What's clarity about your future worth?"}
            </h2>
            <p className="text-base text-stone-500">
              {fr ? "Moins qu'un resto. Plus qu'un déclic." : "Less than a dinner out. More than an epiphany."}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Free */}
            <div className="rounded-2xl border-2 border-stone-200 p-6 text-center hover:shadow-lg transition-all">
              <p className="text-sm font-semibold text-stone-400 mb-2">{fr ? "PARCOURS" : "JOURNEY"}</p>
              <div className="font-serif text-4xl font-bold text-stone-900 mb-1">{fr ? "Gratuit" : "Free"}</div>
              <p className="text-stone-400 text-sm mb-6">{fr ? "Pour toujours" : "Forever"}</p>
              <div className="space-y-3 text-left text-sm">
                {(fr ? ["Inscription", "40 questions d'introspection", "4 modules complets", "Système de niveaux"] : ["Registration", "40 introspection questions", "4 complete modules", "Level system"]).map((f, i) => (
                  <div key={`k-${i}`} className="flex items-center gap-2 text-stone-600"><CheckCircle2 className="w-4 h-4 text-stone-300 flex-shrink-0" />{f}</div>
                ))}
              </div>
              <Button onClick={goStart} variant="outline" className="w-full mt-6 rounded-xl border-stone-200 text-stone-600" data-testid="pricing-free-btn">
                {fr ? "S'inscrire" : "Sign up"}
              </Button>
            </div>

            {/* Diagnostic — Highlighted */}
            <div className="rounded-2xl border-2 border-[#FF6B6B] p-6 text-center relative shadow-xl scale-105 bg-white">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6B6B] text-white text-xs font-bold px-4 py-1 rounded-full">
                {fr ? "POPULAIRE" : "POPULAR"}
              </div>
              <p className="text-sm font-semibold text-[#FF6B6B] mb-2">{fr ? "DIAGNOSTIC IA" : "AI DIAGNOSTIC"}</p>
              <div className="font-serif text-4xl font-bold text-stone-900 mb-1">1,99 <span className="text-lg">€</span></div>
              <p className="text-stone-400 text-sm mb-6">{fr ? "Paiement unique" : "One-time"}</p>
              <div className="space-y-3 text-left text-sm">
                {(fr ? ["Analyse IA personnalisée", "Profil de personnalité", "5 pistes de carrière", "3 projets concrets", "Coaching quotidien IA", "Export PDF"] : ["Personalized AI analysis", "Personality profile", "5 career paths", "3 concrete projects", "Daily AI coaching", "PDF export"]).map((f, i) => (
                  <div key={`k-${i}`} className="flex items-center gap-2 text-stone-700"><CheckCircle2 className="w-4 h-4 text-[#FF6B6B] flex-shrink-0" />{f}</div>
                ))}
              </div>
              <Button onClick={goStart} className="w-full mt-6 bg-[#FF6B6B] hover:bg-[#EE5253] text-white rounded-xl py-3 font-bold" data-testid="pricing-diagnostic-btn">
                {fr ? "Choisir ce plan" : "Choose this plan"}
              </Button>
            </div>

            {/* Plan 90 jours */}
            <div className="rounded-2xl border-2 border-[#1A535C] p-6 text-center bg-[#1A535C] text-white hover:shadow-lg transition-all">
              <p className="text-sm font-semibold text-[#4ECDC4] mb-2">{fr ? "PLAN 90 JOURS" : "90-DAY PLAN"}</p>
              <div className="font-serif text-4xl font-bold mb-1">4,99 <span className="text-lg">€</span></div>
              <p className="text-white/50 text-sm mb-6">{fr ? "Paiement unique" : "One-time"}</p>
              <div className="space-y-3 text-left text-sm">
                {(fr ? ["Tout le Diagnostic IA +", "12 semaines de plan d'action", "Tâches hebdomadaires", "3 phases progressives", "Jalons de progression", "Export PDF du plan"] : ["Everything in AI Diagnostic +", "12 weeks action plan", "Weekly tasks", "3 progressive phases", "Progress milestones", "Plan PDF export"]).map((f, i) => (
                  <div key={`k-${i}`} className="flex items-center gap-2 text-white/80"><CheckCircle2 className="w-4 h-4 text-[#4ECDC4] flex-shrink-0" />{f}</div>
                ))}
              </div>
              <Button onClick={goStart} className="w-full mt-6 bg-[#4ECDC4] hover:bg-[#45b8b0] text-[#1A535C] rounded-xl py-3 font-bold" data-testid="pricing-plan90-btn">
                {fr ? "Choisir ce plan" : "Choose this plan"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Social proof */}
      <section className="py-16 md:py-20 px-4 bg-[#FAFAF9]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-900 mb-10">
            {fr ? "Ils ont trouvé leur voie" : "They found their path"}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {(fr ? [
              { quote: "J'hésitais entre 3 carrières depuis 2 ans. En 40 minutes, l'IA m'a montré ce que je n'arrivais pas à voir. Aujourd'hui je suis en formation pour mon nouveau métier.", name: "Marie, 28 ans", role: "En reconversion" },
              { quote: "Le Plan 90 Jours a transformé mon diagnostic en actions concrètes. Semaine après semaine, j'ai construit mon projet. Ça vaut 100x le prix.", name: "Thomas, 34 ans", role: "Entrepreneur" },
              { quote: "Je ne comprenais pas pourquoi j'étais malheureuse au travail. L'analyse m'a révélé mes vrais talents. C'est comme si quelqu'un avait allumé la lumière.", name: "Sophie, 25 ans", role: "Étudiante" }
            ] : [
              { quote: "I'd been hesitating between 3 careers for 2 years. In 40 minutes, AI showed me what I couldn't see. Today I'm training for my new career.", name: "Marie, 28", role: "Career changer" },
              { quote: "The 90-Day Plan turned my diagnostic into concrete actions. Week after week, I built my project. Worth 100x the price.", name: "Thomas, 34", role: "Entrepreneur" },
              { quote: "I didn't understand why I was unhappy at work. The analysis revealed my true talents. It's like someone turned on the light.", name: "Sophie, 25", role: "Student" }
            ]).map((testimonial, i) => (
              <div key={`k-${i}`} className="bg-white rounded-2xl p-6 border border-stone-100 text-left shadow-sm hover:shadow-md transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#FFE66D] text-[#FFE66D]" />)}
                </div>
                <p className="text-stone-600 text-sm leading-relaxed mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-stone-900 text-sm">{testimonial.name}</p>
                  <p className="text-stone-400 text-xs">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — Emotional close */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-white to-[#FAFAF9]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6">
            {fr ? "Dans 30 jours, tu peux être la même personne. Ou pas." : "In 30 days, you can be the same person. Or not."}
          </h2>
          <p className="text-base md:text-lg text-stone-500 mb-8 max-w-xl mx-auto leading-relaxed">
            {fr 
              ? "1,99\u00A0\u20AC pour découvrir le métier qui te correspond. Le chemin qui te rendra fier. La vie que tu mérites vraiment."
              : "\u20AC1.99 to discover the career that fits you. The path that will make you proud. The life you truly deserve."}
          </p>
          <Button onClick={goStart} className="btn-primary flex items-center gap-3 text-lg px-12 py-5 mx-auto shadow-xl hover:shadow-2xl" data-testid="final-cta-btn">
            {fr ? "Je commence maintenant" : "I start now"}
            <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-stone-400 text-xs mt-4">
            {fr ? "Inscription gratuite. Paiement uniquement pour l'analyse IA." : "Free registration. Payment only for AI analysis."}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-stone-100 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-lg font-semibold text-stone-800">Ikigai</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/cgv')}
              className="text-sm text-stone-400 hover:text-stone-600 underline transition-colors"
              data-testid="footer-cgv-link"
            >
              {fr ? 'Conditions Générales de Vente' : 'Terms & Conditions'}
            </button>
            <p className="text-sm text-stone-500">
              © 2026 Mon Ikigai IA. {fr ? 'Trouve ta voie.' : 'Find your path.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
